import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaCode, FaRocket } from 'react-icons/fa';
import { SiSolidity, SiReact, SiJavascript, SiRust } from 'react-icons/si';

const ProjectsSection = styled.section`
  padding: 5rem 2rem;
  background: rgba(0, 0, 0, 0.2);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #00d4ff;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`;

const ProjectCard = styled(motion.div)`
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: rgba(0, 212, 255, 0.4);
    transform: translateY(-5px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00d4ff, #0099cc);
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ProjectIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled(motion.a)`
  color: #00d4ff;
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    color: #ffffff;
    transform: scale(1.1);
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const ProjectPeriod = styled.div`
  color: #00d4ff;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const ProjectDescription = styled.p`
  color: #b8b8b8;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TechTag = styled.span`
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(0, 212, 255, 0.3);
`;

const ProjectFeatures = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    position: relative;
    padding-left: 1.2rem;
    margin-bottom: 0.5rem;
    color: #b8b8b8;
    font-size: 0.9rem;
    
    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #00d4ff;
      font-weight: bold;
    }
  }
`;

const Projects = () => {
  const projects = [
    {
      title: "TTAVTAR",
      period: "Jan 2023 - November 2024",
      icon: <SiSolidity />,
      description: "A comprehensive blockchain platform with secure smart contracts and rank bonus system for user engagement.",
      features: [
        "Secure smart contract deployment on blockchain",
        "Seamless integration with existing systems and DApps",
        "Linux server management for optimal performance",
        "Rank bonus system for user incentivization"
      ],
      techStack: ["Solidity", "Web3.js", "Linux", "Smart Contracts"],
      github: "https://github.com/satish-mukhekar",
      demo: "https://github.com/satish-mukhekar"
    },
    {
      title: "Trendify Token",
      period: "April 2024",
      icon: <FaCode />,
      description: "Advanced token creation platform with comprehensive smart contract features and security measures.",
      features: [
        "User-friendly interface for token creation and deployment",
        "Mintable, ownership, blacklist, and router features",
        "Trading controls and liquidity tax implementation",
        "Robust security measures and verification processes"
      ],
      techStack: ["Solidity", "React", "Web3.js", "Smart Contracts"],
      github: "https://github.com/satish-mukhekar",
      demo: "https://github.com/satish-mukhekar"
    },
    {
      title: "DEX Platform",
      period: "Feb 2025 - March 2025",
      icon: <SiReact />,
      description: "Decentralized exchange platform with Uniswap integration and cross-chain functionality.",
      features: [
        "Thirdweb SDK integration for on-chain functionalities",
        "Uniswap smart contract integration",
        "Custom liquidity pools and swap mechanisms",
        "Cross-chain interoperability on testnets and mainnets"
      ],
      techStack: ["Thirdweb", "Uniswap", "DeFi", "Cross-chain"],
      github: "https://github.com/satish-mukhekar",
      demo: "https://github.com/satish-mukhekar"
    },
    {
      title: "Layer1 Blockchain Architect",
      period: "April 2025",
      icon: <SiRust />,
      description: "High-performance Layer-1 blockchain node built with Rust for optimal scalability and consensus.",
      features: [
        "Rust-based blockchain node with networking modules",
        "P2P networking and Kademlia DHT implementation",
        "High-performance RPC node for blockchain interaction",
        "Validator logic with consensus participation"
      ],
      techStack: ["Rust", "P2P", "Blockchain", "Consensus", "RPC"],
      github: "https://github.com/satish-mukhekar",
      demo: "https://github.com/satish-mukhekar"
    }
  ];

  return (
    <ProjectsSection id="projects">
      <Container>
        <Title
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Featured Projects
        </Title>

        <ProjectsGrid>
          {projects.map((project, index) => (
            <ProjectCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <ProjectHeader>
                <ProjectIcon>{project.icon}</ProjectIcon>
                <ProjectLinks>
                  <ProjectLink
                    href={project.github}
                    target="_blank"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaGithub />
                  </ProjectLink>
                  <ProjectLink
                    href={project.demo}
                    target="_blank"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaExternalLinkAlt />
                  </ProjectLink>
                </ProjectLinks>
              </ProjectHeader>

              <ProjectTitle>{project.title}</ProjectTitle>
              <ProjectPeriod>{project.period}</ProjectPeriod>
              <ProjectDescription>{project.description}</ProjectDescription>

              <TechStack>
                {project.techStack.map((tech, techIndex) => (
                  <TechTag key={techIndex}>{tech}</TechTag>
                ))}
              </TechStack>

              <ProjectFeatures>
                {project.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>{feature}</li>
                ))}
              </ProjectFeatures>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>
    </ProjectsSection>
  );
};

export default Projects;